import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Button, Grid, Tooltip } from '@material-ui/core';
import * as assetActions from '../../redux/actions/assetsAction';
import { resetTagsData } from '../../redux/actions/tagActions';
import { resetSubmittedAssets } from '../../redux/actions/submittedActions';
import * as stepperActions from '../../redux/actions/stepperActions';
import * as constants from '../../Constants/Stepper';
import { openAlertDialog } from '../../redux/actions/dialogActions';
import { AlertDialogProps } from '../../Utils/DialogUtils';
import * as createConstants from '../../Constants/Create';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import CreateIcon from '@material-ui/icons/Create';

const backbtnLbl = 'Back';
const nextBtnLbl = 'Next';
const createBtnLbl = 'Create';
const createMore3dAssetsBtnTxt = 'Create More Assets';
const exportAssetsAsCsvBtn = 'Export Assets';
const errorDialogContent = 'Please fix the errors in this page before navigating to next page.';
const assetNameEmptyErrorContent = 'Please fill in the asset(s) names.';
const no3DAssetsErrorDialogMsg = 'Please add 3D assets before navigation to the next page.';
const errorDialogTitle = 'Errors';
const createBtnToolTipMsg = 'Click to add the assets to the table below for Corgis processing.';

// Render button container. which manages display of back,next,submit and createmore3dassets buttons.
// basing on page layout.
const BackNextButtonContainer = () => {
	const dispatch = useDispatch();
	const store = useStore();
	const steps = createConstants.manualEntryStepperSteps;
	const stepperState = useSelector((state) => state.stepper);
	// asset successfully created confirmation page. after submit.
	const successfullyCreated3dAssets = useSelector((state) => state.submittedAssets);
	const displayAfterSubmitView = successfullyCreated3dAssets.length > 0;
	const backButtonDisable = stepperState.activeStep === constants.uploadFilesStepIndex ? true : false;
	const nxtBtnDisable = stepperState.activeStep === steps.length ? true : false;
	//Checking page error status and displaying dialog accordingly.
	const CheckToDisplayErrorDialog = () => {
		//checking if error exists (invalid asset name)
		const assets = store.getState().assets;
		let dialogContent = '';
		const assetNameInvalidError = assets.some(
			(asset) => asset.isAssetNameUnqiueOnUI === false || asset.isAssetNameUniqueInBackEnd === false,
		);
		const assetNameEmptyError = assets.some((asset) => asset.name.length === 0);
		//checking if assets are added on the UI before going to next step.
		const no3DAssets = assets.length === 0;
		const errorState = assetNameInvalidError || assetNameEmptyError || no3DAssets;
		//checking if any error exists and displaying a dialog
		if (no3DAssets) {
			dialogContent = no3DAssetsErrorDialogMsg;
		} else if (assetNameEmptyError) {
			dialogContent = assetNameEmptyErrorContent;
		} else if (assetNameInvalidError) {
			dialogContent = errorDialogContent;
		}
		if (errorState) {
			dispatch(openAlertDialog(new AlertDialogProps(dialogContent, errorDialogTitle)));
		}
		return errorState;
	};
	//after submit, asset creation confirmation page has to be displayed.
	const handleSubmitBtnClick = async () => {
		const assets = store.getState().assets;
		const createJira = store.getState().shouldCreateJiraTicket;
		dispatch(assetActions.createCustom3dAssets(assets, stepperState.activeStep, createJira));
	};
	//back and next button handlers to move forward
	const backBtnClickHandler = () => {
		dispatch(stepperActions.setActiveStep(stepperState.activeStep - 1));
	};
	//next button click event increments the stepper step to navigate to next page.
	const NextBtnClickHandler = () => {
		//checking if any error exists and displaying a dialog
		if (!CheckToDisplayErrorDialog()) {
			dispatch(stepperActions.setActiveStep(stepperState.activeStep + 1));
		}
	};

	const handleCreateMore3dAssetsBtnClick = () => {
		//Resetting the asset redux state.
		dispatch(assetActions.resetAssetData());
		//Resetting tags redux state.
		dispatch(resetTagsData());
		//Resetting submitted assets state
		dispatch(resetSubmittedAssets());
		//setting stepper count to 1. which renders uploadfiles view
		dispatch(stepperActions.setActiveStep(constants.uploadFilesStepIndex));
	};
	const handleExportAssetsBtnClick = () => {
		// Export the created assets as CSV.
		assetActions.exportCreatedAssetsAsCSV(successfullyCreated3dAssets);
	};

	if (displayAfterSubmitView) {
		return (
			<Grid container spacing={2}>
				<Grid item>
					<Button
						variant="contained"
						color="primary"
						onClick={handleCreateMore3dAssetsBtnClick}
						startIcon={<CreateIcon />}
						size="small"
					>
						{createMore3dAssetsBtnTxt}
					</Button>
				</Grid>
				<Grid item>
					<Button
						variant="contained"
						color="primary"
						onClick={handleExportAssetsBtnClick}
						startIcon={<ArrowDownwardIcon />}
						size="small"
					>
						{exportAssetsAsCsvBtn}
					</Button>
				</Grid>
			</Grid>
		);
	} else {
		return (
			<Grid container spacing={3}>
				<Grid item>
					<Button
						variant="contained"
						color="secondary"
						data-cy="back-Btn"
						onClick={backBtnClickHandler}
						disabled={backButtonDisable}
					>
						{backbtnLbl}
					</Button>
				</Grid>
				{stepperState.activeStep === constants.reviewAndSubmitStepIndex ? (
					<Grid item>
						<Tooltip title={createBtnToolTipMsg} arrow={true} placement="top">
							<Button
								variant="contained"
								color="primary"
								data-cy="submit-Btn"
								onClick={handleSubmitBtnClick}
								startIcon={<CheckCircleIcon />}
								// Disable submit button when assets list is empty
								disabled={store.getState().assets.length === 0}
							>
								{createBtnLbl}
							</Button>
						</Tooltip>
					</Grid>
				) : (
					<Grid item>
						<Button
							variant="contained"
							color="secondary"
							data-cy="next-Btn"
							onClick={NextBtnClickHandler}
							disabled={nxtBtnDisable}
						>
							{nextBtnLbl}
						</Button>
					</Grid>
				)}
			</Grid>
		);
	}
};

export default BackNextButtonContainer;
